import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Copy, Check, Share2, Users, Gift, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMXN } from "@/lib/format";

export default function ReferralsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?redirect=/referidos");
      return;
    }
    api.get("/referrals/me").then((r) => {
      setData(r.data);
      setBusy(false);
    }).catch(() => setBusy(false));
  }, [user, loading, navigate]);

  if (busy || !data) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="font-display text-2xl text-white/50 tracking-wider">Cargando referidos...</div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/login?ref=${data.code}`;
  const whatsappMsg = encodeURIComponent(
    `¡Qué onda compare! Te paso mi código de Arre Sonora: ${data.code}. Regístrate con mi enlace y nos llevamos $${data.reward_per_referral} cada uno: ${shareUrl}`
  );

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="bg-arre-black min-h-screen py-16 px-6" data-testid="referrals-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Programa Compare</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none mb-4">
          Invita a un<br /><span className="text-arre-red">compare</span>
        </h1>
        <p className="text-white/60 max-w-xl mb-12">
          Comparte tu código. Cuando tu compa haga su primera compra de ${data.min_order_amount.toFixed(0)}+,
          ambos se llevan ${data.reward_per_referral.toFixed(0)} pesos de saldo Arre.
        </p>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Stat icon={Gift} label="Saldo Arre" value={fmtMXN(data.credit_balance)} highlight testid="ref-credit" />
          <Stat icon={Users} label="Invitados" value={data.total_signups} testid="ref-signups" />
          <Stat icon={TrendingUp} label="Conversiones" value={data.total_conversions} testid="ref-conversions" />
          <Stat icon={Gift} label="Total ganado" value={fmtMXN(data.total_earned)} testid="ref-earned" />
        </div>

        {/* Code card */}
        <div className="bg-arre-surface border border-arre-border p-8 mb-10">
          <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 mb-3">Tu código compare</div>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="font-display text-4xl md:text-5xl tracking-wider text-arre-red" data-testid="ref-code">
              {data.code}
            </div>
            <button
              onClick={() => copy(data.code)}
              className="btn-arre border border-arre-border hover:border-arre-red text-white font-display text-xs tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copiado" : "Copiar"}
            </button>
          </div>

          <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 mb-3">Tu enlace</div>
          <div className="flex gap-2 mb-4">
            <input
              value={shareUrl}
              readOnly
              className="flex-1 bg-arre-black border border-arre-border px-4 py-3 text-white text-sm font-mono"
              data-testid="ref-link"
            />
            <button
              onClick={() => copy(shareUrl)}
              className="btn-arre bg-arre-red text-white font-display text-xs tracking-widest uppercase px-4"
              data-testid="copy-link-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          <a
            href={`https://wa.me/?text=${whatsappMsg}`}
            target="_blank"
            rel="noreferrer"
            className="btn-arre w-full md:w-auto bg-[#25D366] hover:opacity-90 text-white font-display text-base tracking-widest uppercase px-6 py-3 inline-flex items-center justify-center gap-2"
            data-testid="share-whatsapp-btn"
          >
            <Share2 className="w-4 h-4" /> Compartir por WhatsApp
          </a>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {[
            { n: "01", t: "Comparte tu código", d: "Mándalo por WhatsApp, redes o donde quieras." },
            { n: "02", t: "Tu compa se registra", d: "Usando tu código en el registro." },
            { n: "03", t: "Ambos ganan", d: `Cuando haga su primera compra de $${data.min_order_amount.toFixed(0)}+, los dos se llevan $${data.reward_per_referral.toFixed(0)} de saldo.` },
          ].map((s) => (
            <div key={s.n} className="border border-arre-border p-6 bg-arre-surface">
              <div className="text-arre-red font-display text-3xl mb-2">{s.n}</div>
              <div className="font-display text-xl tracking-wider uppercase text-white mb-1">{s.t}</div>
              <p className="text-white/60 text-sm">{s.d}</p>
            </div>
          ))}
        </div>

        {/* Signups list */}
        <h2 className="font-display text-3xl tracking-wider uppercase text-white mb-5">Tus invitados</h2>
        {data.signups.length === 0 ? (
          <div className="bg-arre-surface border border-arre-border p-8 text-center">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="font-display text-2xl tracking-wider uppercase text-white">Aún no invitas a nadie</p>
            <p className="text-white/60 text-sm mt-1">Empieza compartiendo tu código.</p>
          </div>
        ) : (
          <div className="bg-arre-surface border border-arre-border" data-testid="signups-list">
            {data.signups.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 border-b border-arre-border last:border-0">
                <div>
                  <div className="font-display text-base text-white">{s.name}</div>
                  <div className="text-xs text-white/40">{s.email}</div>
                </div>
                <div>
                  {s.converted ? (
                    <span className="text-xs tracking-[0.2em] uppercase bg-green-900/30 border border-green-600/50 text-green-400 px-3 py-1">
                      Convertido
                    </span>
                  ) : (
                    <span className="text-xs tracking-[0.2em] uppercase bg-yellow-900/30 border border-yellow-600/50 text-yellow-400 px-3 py-1">
                      Pendiente
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/cuenta" className="text-white/50 hover:text-white text-sm tracking-widest uppercase">
            ← Volver a mi cuenta
          </Link>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div
      className={`p-5 border ${highlight ? "border-arre-red bg-arre-red/5" : "border-arre-border bg-arre-surface"}`}
      data-testid={testid}
    >
      <Icon className={`w-5 h-5 mb-2 ${highlight ? "text-arre-red" : "text-white/50"}`} />
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{label}</div>
      <div className="font-display text-2xl text-white mt-1">{value}</div>
    </div>
  );
}
